import axios from 'axios'
import { Capacitor } from '@capacitor/core'
import { getCapacitorAdapter } from './api/httpAdapter'
import { HTTP_URLS } from '@/config/http'

const API_BASE_URL = HTTP_URLS.api
const SANCTUM_BASE_URL = HTTP_URLS.sanctum
const BACKEND_RESET_PASSWORD_URL = `${HTTP_URLS.backend}/app/reset-password`

function isNativeOrMobileApp() {
  try {
    // Evita falsos positivos no navegador quando VITE_IS_MOBILE_APP=true no build web.
    // Para comportamento mobile, confiamos apenas na detecção real do Capacitor.
    return Capacitor.isNativePlatform()
  } catch {
    return false
  }
}

const authAdapter = getCapacitorAdapter()

function buildAuthRequestConfig(timeout = 15000) {
  const csrfToken = getCsrfToken()

  return {
    timeout,
    // Em runtime nativo, cookies cross-site do Sanctum podem travar requisições no WebView.
    withCredentials: !isNativeOrMobileApp(),
    headers: csrfToken ? { 'X-XSRF-TOKEN': csrfToken } : {}
  }
}

function buildPasswordResetRedirectUrl() {
  if (isNativeOrMobileApp()) {
    // Em runtime mobile, manter sempre apontando para produção.
    return BACKEND_RESET_PASSWORD_URL
  }

  const envUrl = import.meta.env.VITE_PASSWORD_RESET_REDIRECT_URL
  if (envUrl) {
    return String(envUrl).replace(/\/+$/, '')
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    const base = (import.meta.env.BASE_URL || '/').replace(/\/+$/, '')
    const normalizedBase = base && base !== '/' ? base : ''
    return `${window.location.origin}${normalizedBase}/reset-password`
  }

  return BACKEND_RESET_PASSWORD_URL
}

// Configurar axios
const api = axios.create({
  baseURL: API_BASE_URL,
  // Evitar requests "pendurados" no WebView do Capacitor
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...(isNativeOrMobileApp() ? {} : { 'X-Requested-With': 'XMLHttpRequest' })
  },
  withCredentials: !isNativeOrMobileApp(), // Necessário para Sanctum apenas no navegador
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN',
  ...(authAdapter ? { adapter: authAdapter } : {})
})

/**
 * Obtém o token CSRF do cookie XSRF-TOKEN
 */
function getCsrfToken() {
  const cookies = document.cookie.split(';')
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=')
    if (name === 'XSRF-TOKEN') {
      try {
        return decodeURIComponent(value)
      } catch (e) {
        return value
      }
    }
  }
  return null
}

/**
 * Normaliza payloads de autenticação aceitando:
 * - { data: { token, ... } } (formato legado)
 * - { token, ... } (formato direto)
 */
function normalizeAuthPayload(responseData) {
  if (!responseData || typeof responseData === 'string') {
    return null
  }

  if (responseData.data && typeof responseData.data === 'object') {
    return responseData.data
  }

  return responseData
}

function buildInvalidAuthResponse(responseData, status, operation) {
  const isHtmlResponse = typeof responseData === 'string' &&
    /<!doctype html|<html/i.test(responseData)

  if (isHtmlResponse) {
    return {
      success: false,
      status,
      errors: {},
      message: `Resposta inválida ao ${operation}: a API retornou HTML em vez de JSON. Verifique configuração do Capacitor/URL da API no app.`,
      isInvalidPayload: true
    }
  }

  return {
    success: false,
    status,
    errors: responseData?.errors || {},
    message: responseData?.message || `Resposta inválida ao ${operation}.`,
    isInvalidPayload: true
  }
}

// Interceptor para adicionar token automaticamente
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Adicionar token CSRF para requisições stateful (Sanctum)
    const csrfToken = getCsrfToken()
    if (!isNativeOrMobileApp() && csrfToken) {
      config.headers['X-XSRF-TOKEN'] = csrfToken
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Interceptor para tratar respostas
// NOTA: O refresh de token é tratado centralmente em src/services/api/interceptors/response.ts
// para evitar múltiplos interceptores concorrentes e loop infinito
api.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    // Não fazer nada aqui - deixar os interceptores centrais tratarem os erros
    // Isso evita loop infinito de refresh token
    return Promise.reject(error)
  }
)

export const authService = {
  // Configurar token de autenticação
  setAuthToken(token) {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`
    } else {
      delete api.defaults.headers.common['Authorization']
    }
  },

  // Obter cookie CSRF do Sanctum antes de fazer login
  async getCsrfCookie() {
    if (isNativeOrMobileApp()) {
      console.log('[CSRF] Ignorando cookie CSRF em runtime mobile nativo.')
      return false
    }

    // Ordem: absoluto primeiro (evita cair no Vite devolvendo index.html), depois relativo (proxy).
    const csrfEndpoints = [
      `${SANCTUM_BASE_URL}/sanctum/csrf-cookie`,
      '/sanctum/csrf-cookie'
    ]

    for (const url of csrfEndpoints) {
      try {
        await axios.get(url, {
          withCredentials: true,
          timeout: 5000,
          baseURL: url.startsWith('http') ? undefined : SANCTUM_BASE_URL
        })
        const token = getCsrfToken()
        if (token) {
          return true
        }
      } catch {
        console.warn('[CSRF] Não foi possível obter o cookie')
      }
    }

    return false
  },

  // Login
  async login(credentials) {
    try {
      const useCsrf = !isNativeOrMobileApp()
      if (useCsrf) {
        const csrfSuccess = await this.getCsrfCookie()
        const csrfToken = getCsrfToken()
        if (!csrfSuccess || !csrfToken) {
          console.warn('Aviso: Não foi possível obter cookie CSRF ou token está ausente. Tentando assim mesmo...')
        }
      }

      const response = await api.post('/auth/login', credentials, buildAuthRequestConfig())

      const responseData = response?.data
      const authPayload = normalizeAuthPayload(responseData)

      if (!authPayload?.token) {
        console.error('[Login] Resposta inesperada da API', {
          status: response?.status,
          contentType: response?.headers?.['content-type'],
          dataType: typeof responseData,
          hasToken: !!authPayload?.token
        })

        return buildInvalidAuthResponse(responseData, response?.status, 'login')
      }

      return {
        success: true,
        data: authPayload,
        message: responseData?.message || 'Login realizado com sucesso'
      }
    } catch (error) {
      console.error('Erro no login', { status: error.response?.status ?? null })

      // Tratamento específico para erros CORS
      if (!error.response) {
        // Erro de rede ou CORS
        if (error.message?.includes('CORS') || error.code === 'ERR_NETWORK') {
          return {
            success: false,
            message: 'Erro de conexão. Verifique se o servidor está rodando e a configuração de CORS está correta.',
            errors: {},
            isCorsError: true
          }
        }

        return {
          success: false,
          message: 'Erro de conexão. Verifique sua conexão com a internet.',
          errors: {},
          isNetworkError: true
        }
      }

      // Tratamento para erro 500
      if (error.response?.status === 500) {
        return {
          success: false,
          message: 'Erro interno do servidor. Tente novamente mais tarde ou entre em contato com o suporte.',
          errors: error.response?.data?.errors || {},
          isServerError: true
        }
      }

      // Tratamento para outros erros HTTP
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao fazer login',
        errors: error.response?.data?.errors || {},
        status: error.response?.status
      }
    }
  },

  // Registro
  async register(userData) {
    try {
      const useCsrf = !isNativeOrMobileApp()
      if (useCsrf) {
        const csrfSuccess = await this.getCsrfCookie()
        const csrfToken = getCsrfToken()
        if (!csrfSuccess || !csrfToken) {
          console.warn('Aviso: Não foi possível obter cookie CSRF. Tentando assim mesmo...')
        }
      }

      const response = await api.post('/auth/register', userData, buildAuthRequestConfig())

      const responseData = response?.data
      const authPayload = normalizeAuthPayload(responseData)

      if (!authPayload?.token) {
        console.error('[Register] Resposta inesperada da API', {
          status: response?.status,
          contentType: response?.headers?.['content-type'],
          dataType: typeof responseData,
          hasToken: !!authPayload?.token
        })

        return buildInvalidAuthResponse(responseData, response?.status, 'registro')
      }

      return {
        success: true,
        data: authPayload,
        message: responseData?.message || 'Conta criada com sucesso'
      }
    } catch (error) {
      console.error('Erro no registro', { status: error.response?.status ?? null })

      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao criar conta',
        errors: error.response?.data?.errors || {},
        status: error.response?.status
      }
    }
  },

  // Solicitar recuperação de senha
  async forgotPassword(email) {
    try {
      const payload = {
        email,
        redirect_url: buildPasswordResetRedirectUrl()
      }

      const response = await api.post('/auth/forgot-password', payload, { timeout: 15000 })

      return {
        success: true,
        data: response?.data?.data || null,
        message: response?.data?.message || 'Se o email existir, você receberá o link de recuperação.'
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao solicitar recuperação de senha',
        errors: error.response?.data?.errors || {},
        status: error.response?.status
      }
    }
  },

  // Redefinir senha
  async resetPassword(data) {
    try {
      const response = await api.post('/auth/reset-password', data, { timeout: 15000 })

      return {
        success: true,
        data: response?.data?.data || null,
        message: response?.data?.message || 'Senha redefinida com sucesso'
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao redefinir senha',
        errors: error.response?.data?.errors || {},
        status: error.response?.status
      }
    }
  },

  // Logout
  async logout() {
    try {
      await api.post('/auth/logout')

      return {
        success: true,
        message: 'Logout realizado com sucesso'
      }
    } catch (error) {
      // Se o erro for 401, o token já foi invalidado ou não existe
      // Isso é esperado em alguns casos, então consideramos sucesso
      if (error.response?.status === 401) {
        return {
          success: true,
          message: 'Logout realizado com sucesso'
        }
      }

      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao fazer logout'
      }
    }
  },

  // Buscar dados do usuário
  async getUser() {
    try {
      const response = await api.get('/auth/me')

      return {
        success: true,
        data: response.data.data
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao buscar dados do usuário'
      }
    }
  },

  // Renovar token
  async refreshToken() {
    try {
      const response = await api.post('/auth/refresh')

      return {
        success: true,
        data: response.data.data
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao renovar token'
      }
    }
  },

  // Verificar se está autenticado
  async checkAuth() {
    try {
      const response = await api.get('/auth/me')
      return response.data.success
    } catch (error) {
      return false
    }
  },

  // Validar token
  async validateToken() {
    try {
      const response = await api.get('/auth/me')

      return {
        success: true,
        data: response.data.data
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Token inválido'
      }
    }
  }
}
